import { ref, computed, onMounted } from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import {
  fetchSurveyData,
  submitSurveyAnswers,
  isSurveyBackendValidationError,
} from "@/services/survey";
import nativeHandlerService from "@/services/nativeHandler";

/**
 * アンケート画面（Survey / ShareholderSurvey）共通のデータ取得・回答送信・遷移ロジック
 */
export function useSurveyPage() {
  const store = useStore();
  const route = useRoute();
  const router = useRouter();
  const dialogLoading = ref(null);
  const dialogError = ref(null);
  const masterSurvey = ref(null);
  const hasBackendValidationError = ref(false);

  const masterBook = computed(() => store.getters["ticketing/masterBook"]);
  const userInfoAcquisitionTermsUrl = computed(
    () =>
      masterBook.value?.content?.description_json
        ?.UserInfoAcquisitionTermsUrl ?? null
  );

  onMounted(async () => {
    dialogLoading.value.openDialog();
    try {
      masterSurvey.value = await fetchSurveyData(
        route.params.master_survey_id,
        route.params.master_book_id
      );
    } catch {
      dialogError.value.openDialog();
    } finally {
      dialogLoading.value.closeDialog();
    }
  });

  const redirectAfterSubmit = () => {
    if (route.query.voucher) {
      router.push({
        name: "BookBookingResult",
        params: {
          id: route.params.master_book_id,
          book_group_id: route.params.book_group_id,
        },
        query: {
          voucher: true,
          language: store.getters["common/lang"],
        },
      });
      return;
    }
    router.push({
      name: "BookBookingConfirmation",
      params: {
        id: route.params.master_book_id,
        book_group_id: route.params.book_group_id,
      },
    });
  };

  const onSurveySubmit = async (answers) => {
    dialogLoading.value.openDialog();
    hasBackendValidationError.value = false;
    try {
      await submitSurveyAnswers(
        route.params.book_group_id,
        masterSurvey.value.question,
        answers
      );
    } catch (err) {
      if (isSurveyBackendValidationError(err)) {
        hasBackendValidationError.value = true;
      } else {
        dialogError.value.openDialog();
      }
      return;
    } finally {
      dialogLoading.value.closeDialog();
    }
    redirectAfterSubmit();
  };

  return {
    masterSurvey,
    userInfoAcquisitionTermsUrl,
    hasBackendValidationError,
    dialogLoading,
    dialogError,
    nativeHandlerService,
    onSurveySubmit,
  };
}
